<!-- eslint-disable vue/multi-word-component-names -->
<script setup>
import { ref, computed, watch, h } from 'vue'
import UserTag from './UserTag.vue'
import UserPicker from './UserPicker.vue'
import { useFormDisabled, useFormSize } from 'element-plus'
import { getUser } from '@/api/user'

// 用户添加图标组件 - 复用项目已有的内联SVG模式
const UserAddIcon = {
  render() {
    return h('svg', {
      fill: 'none',
      stroke: 'currentColor',
      viewBox: '0 0 24 24',
      class: 'w-4 h-4'
    }, [
      h('path', {
        'stroke-linecap': 'round',
        'stroke-linejoin': 'round',
        'stroke-width': '2',
        d: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z'
      })
    ])
  }
}

const $props = defineProps({
  modelValue: {
    type: [Number, String, Array],
    default: undefined
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择用户'
  },
  maxSelect: {
    type: Number,
    default: null
  },
  excludeIds: {
    type: Array,
    default: () => []
  },
  hideAddButton: {
    type: Boolean,
    default: false
  }
})
const $emits = defineEmits(['update:modelValue'])

// 用户信息映射表，用于编辑时根据ID查找用户信息
const userMap = ref({})

const normalizeUser = (user, id) => {
  const data = user?.data || user || {}
  return {
    id: data.id ?? id,
    name: data.displayName || data.name || data.username || String(id),
    avatar: data.avatarUrl || data.avatar
  }
}

// 加载用户信息到映射表
const loadUserInfo = async (ids) => {
  if (!ids || ids.length === 0) return
  try {
    const missingIds = ids.filter(id => id && !userMap.value[id])
    const users = await Promise.all(missingIds.map(async id => {
      try {
        return normalizeUser(await getUser(id), id)
      } catch (error) {
        console.error('加载用户信息失败:', error)
        return { id, name: String(id), avatar: '' }
      }
    }))
    users.forEach(user => {
      userMap.value[user.id] = user
    })
  } catch (error) {
    console.error('加载用户信息失败:', error)
  }
}

// 监听modelValue变化，加载用户信息
watch(() => $props.modelValue, (val) => {
  if (!val) return
  const ids = Array.isArray(val) ? val : [val]
  // 如果有纯ID需要查找用户信息
  const needLoad = ids.some(id => typeof id === 'number' && !userMap.value[id])
  if (needLoad) {
    loadUserInfo(ids)
  }
}, { immediate: true })

const valueArr = computed(() => {
  if (!$props.modelValue) return []
  const arr = Array.isArray($props.modelValue) ? $props.modelValue : [$props.modelValue]
  return arr.map(item => {
    if (typeof item === 'object') {
      return item
    }
    // 纯ID时尝试从映射表获取信息
    return userMap.value[item] || { id: item, name: '', avatar: '' }
  })
})
const userPickerRef = ref()
const formDisabled = useFormDisabled()
const formSize = useFormSize()
const disabled = computed(() => {
  return formDisabled.value || $props.disabled
})
const openUserPicker = () => {
  userPickerRef.value?.open()
}
const onPickerConfirm = (selectedUsers) => {
  selectedUsers.forEach(user => {
    if (user.id) {
      userMap.value[user.id] = { id: user.id, name: user.name || '', avatar: user.avatar || '' }
    }
  })
}
const onClose = (id) => {
  if (!$props.modelValue) return
  if ($props.multiple && Array.isArray($props.modelValue)) {
    const newValue = $props.modelValue.filter(item => {
      const itemId = typeof item === 'object' ? item.id : item
      return String(itemId) !== String(id)
    })
    $emits('update:modelValue', newValue)
  } else {
    $emits('update:modelValue', null)
  }
}
</script>

<template>
  <user-picker ref="userPickerRef" :multiple="multiple" :model-value="modelValue" :max-select="maxSelect" :exclude-ids="excludeIds" @update:modelValue="$emit('update:modelValue', $event)" @confirm="onPickerConfirm" />
  <div class="user-wrapper">
    <el-button
      v-if="!hideAddButton"
      class="user-but-item"
      :size="formSize"
      :disabled="disabled"
      @click="openUserPicker"
      circle
    >
      <UserAddIcon />
    </el-button>
    <user-tag
      v-for="item in valueArr"
      :closable="!disabled"
      :key="typeof item === 'object' ? item.id : item"
      :id="typeof item === 'object' ? item.id : item"
      :name="typeof item === 'object' ? item.name : ''"
      :avatar="typeof item === 'object' ? item.avatar : ''"
      @close="onClose(typeof item === 'object' ? item.id : item)"
    />
    <el-text v-show="!modelValue || (Array.isArray(modelValue) && modelValue.length === 0)" class="placeholder">
      {{ placeholder }}
    </el-text>
  </div>
</template>

<style scoped lang="scss">
.el-tag {
  padding: 0 3px;
}

.user-wrapper {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  grid-gap: 7px;
  gap: 7px;
  min-width: 0;
  max-height: 72px;
  overflow-y: auto;

  .placeholder {
    color: var(--el-text-color-placeholder);
  }

  .user-but-item {
    border-style: dashed;

    &:hover {
      border-style: solid;
    }
  }
}
</style>
